/**
 * Tiny, safe evaluator for the `enabled_when` business-rule column.
 *
 * Supported grammar (deliberately small for the POC - see docs for the
 * upgrade path to a full rule engine such as json-logic-js):
 *   expr        := comparison (("&&" | "||") comparison)*
 *   comparison  := IDENT "==" LITERAL | IDENT "!=" LITERAL
 *   LITERAL     := "true" | "false" | "quoted string" | number
 *
 * Examples from the schema:
 *   heating_method == "infrared"
 *   balayage_enabled == true
 */
export type HMIFieldValues = Record<string, unknown>;

const COMPARISON_RE = /^\s*([a-zA-Z0-9_]+)\s*(==|!=)\s*(.+?)\s*$/;

function parseLiteral(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  const quoted = trimmed.match(/^"(.*)"$/) || trimmed.match(/^'(.*)'$/);
  if (quoted) return quoted[1];
  return trimmed;
}

function evaluateComparison(expr: string, values: HMIFieldValues): boolean {
  const match = expr.match(COMPARISON_RE);
  if (!match) {
    // Fail open (enabled) if the rule can't be parsed - never hard-lock a
    // control because of a schema authoring mistake.
    // eslint-disable-next-line no-console
    console.warn(`[hmi] Unable to parse enabled_when clause: "${expr}"`);
    return true;
  }
  const [, key, operator, rawLiteral] = match;
  const literal = parseLiteral(rawLiteral);
  const actual = values[key];
  // eslint-disable-next-line eqeqeq
  const isEqual = actual == literal;
  return operator === '==' ? isEqual : !isEqual;
}

/**
 * Evaluates an `enabled_when` expression against the current field values.
 * Returns `true` (enabled) when the expression is empty/undefined.
 */
export function evaluateEnabledWhen(
  expression: string | undefined,
  values: HMIFieldValues
): boolean {
  if (!expression || !expression.trim()) return true;

  const orGroups = expression.split('||');
  return orGroups.some((orGroup) =>
    orGroup
      .split('&&')
      .every((clause) => evaluateComparison(clause, values))
  );
}

/** Client-side range/type validation mirrored from the `range_min`/`range_max` columns. */
export function validateRange(
  value: number,
  rangeMin?: number,
  rangeMax?: number
): string | undefined {
  if (rangeMin !== undefined && value < rangeMin) {
    return `Value must be \u2265 ${rangeMin}`;
  }
  if (rangeMax !== undefined && value > rangeMax) {
    return `Value must be \u2264 ${rangeMax}`;
  }
  return undefined;
}
